import ProcesosTopSection from 'components/modules/project-management/ProcesosTopSection';
import ProcesosListTable, {
  procesosListTableColumns
} from 'components/tables/ProcesosListTable';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { useAuth } from 'providers/AuthProvider';
import { User } from 'providers/AuthProvider';
import { Proceso } from 'data/project-management/procesos'; 

const ProjectListView = () => {
  const [procesos, setProcesos] = useState<Proceso[]>([]);
  const [showUnsupervisedOnly, setShowUnsupervisedOnly] = useState(false);
  const [showAllProcesos, setShowAllProcesos] = useState(false); 
  const { user } = useAuth();
  
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:2000';
  
  const baseEndpoint = '/procesos/procesosajson';
  const unsupervisedEndpoint = '/procesos/unsupervised';
  const userSpecificEndpoint = '/procesos/procesosUser';

  const fetchProcesos = useCallback(async () => {
    try {
      const currentUser = user as User | null;
      let url = '';
      
      const config: { params: Record<string, string | undefined> } = { 
        params: {} 
      };

      const isSocio = currentUser?.nivel_acceso === 'socio';

      if (isSocio || showAllProcesos) {
        const endpoint = (isSocio && showUnsupervisedOnly) ? unsupervisedEndpoint : baseEndpoint;
        url = `${apiUrl}${endpoint}`;
      } else {
        url = `${apiUrl}${userSpecificEndpoint}`;
        config.params.initials = currentUser?.iniciales;
      }

      const response = await axios.get<Proceso[]>(url, config);
      setProcesos(response.data);
      
    } catch (error) {
      console.error('Error fetching procesos:', error);
    }
  }, [user, showUnsupervisedOnly, showAllProcesos, apiUrl, unsupervisedEndpoint, baseEndpoint, userSpecificEndpoint]);

  useEffect(() => {
    if (user) {
      fetchProcesos();
    }
  }, [fetchProcesos, user]);

  const tableColumns = procesosListTableColumns.filter(column => {
    const col = column as { accessorKey?: string };
    if (col.accessorKey === 'MIEM') {
      return user?.nivel_acceso === 'socio' || showAllProcesos;
    }
    return true; 
  });

  const table = useAdvanceTable({
    data: procesos,
    columns: tableColumns,
    pageSize: 2400,
    pagination: true,
    sortable: true,
  });

  const countOverdue = procesos.filter((p: Proceso) => {
    const days = p.dias_sin_mto;
    
    if (days === undefined && p.fecha_supervision) {
        const lastDate = new Date(p.fecha_supervision);
        const today = new Date();
        const diffTime = Math.abs(today.getTime() - lastDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 90;
    }

    return (days ?? 0) > 90;
  }).length;

  return (
    <div>
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-4 gap-3 gap-sm-6 align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <h2 className="mb-0">
              <span className="me-3">
                {user?.nivel_acceso === 'socio' || showAllProcesos ? 'Todos los Procesos' : 'Mis Procesos'}
              </span>
              {/* Nueva validación: Solo renderiza el número si es socio o si el no-socio está viendo "Mis Procesos" */}
              {(user?.nivel_acceso === 'socio' || !showAllProcesos) && (
                <span className="fw-normal text-body-tertiary">
                  ({procesos.length})
                </span>
              )}
            </h2>

            {user?.nivel_acceso === 'socio' ? (
              <Button
                variant={showUnsupervisedOnly ? 'primary' : 'outline-secondary'}
                onClick={() => setShowUnsupervisedOnly(!showUnsupervisedOnly)}
              >
                {showUnsupervisedOnly
                  ? 'Mostrar todos los procesos'
                  : 'Mostrar solo no supervisados (>90 días)'}
              </Button>
            ) : (
              <Button
                variant="outline-secondary"
                onClick={() => setShowAllProcesos(!showAllProcesos)}
              >
                {showAllProcesos
                  ? 'Ver solo mis procesos'
                  : 'Ver todos los procesos'}
              </Button>
            )}
          </div>
        </div>
        <div className="d-flex align-items-center gap-2 mb-3">
          <span className="badge badge-phoenix badge-phoenix-danger fs-8">
            {countOverdue}
          </span>
          <span className="fw-bold text-body-highlight">
            Sin Movimientos &gt; 90 días
          </span>
        </div>

        <ProcesosTopSection activeView="list" />
        <ProcesosListTable />
      </AdvanceTableProvider>
    </div>
  );
};

export default ProjectListView;